import React from 'react';
import { Table, Button, Space, TableColumnsType } from 'antd';
import { PickupRouteWithVehicleInfoType } from '../graphql/types/PickupRouteTypes';
import { useTotalZipcodesCount } from '../hooks/useTotalZipcodesCount';
import { formatISODateTime } from 'utils/dateTimeUtils';
import { useTranslation } from 'react-i18next';
import { hasResource } from '../../../../utils/resourceAuth';
import { ResourceCode } from '../../../../constants/resourceCodes';

interface PickupTaskPlanningTableProps {
    data: PickupRouteWithVehicleInfoType[];
    loading?: boolean;
    onShowRouteDetail?: (routeData: PickupRouteWithVehicleInfoType) => void;
    onEditRoute?: (routeData: PickupRouteWithVehicleInfoType) => void;
    onDeleteRoute?: (routeData: PickupRouteWithVehicleInfoType) => void;
    onViewUndispatchedAppointments?: (routeData: PickupRouteWithVehicleInfoType) => void;
    warehouseId?: number;
    onTotalCountRefetch?: () => void;
    disableEdit?: boolean;
    disableDelete?: boolean;
}

const PickupTaskPlanningTable: React.FC<PickupTaskPlanningTableProps> = ({
    data,
    loading,
    onShowRouteDetail,
    onEditRoute,
    onDeleteRoute,
    onViewUndispatchedAppointments,
    warehouseId,
    onTotalCountRefetch,
    disableEdit = false,
    disableDelete = false
}) => {

    const { totalCount } = useTotalZipcodesCount(warehouseId || 0);
    const {t} = useTranslation();
    // 未调度预约单点击后跳转的是预约单菜单，没有该菜单权限时按钮不可点击
    const pickupAppointment = hasResource([ResourceCode.menu.PICKUP_APPOINTMENT]);
    const columns: TableColumnsType<PickupRouteWithVehicleInfoType> = [
        {
            title: t('pickup.pickupTask.columns.id'),
            dataIndex: 'id',
            key: 'id',
            width: 100,
            align: 'left',
        },
        {
            title: t('pickup.pickupTask.columns.route'),
            dataIndex: 'routeName',
            key: 'routeName',
            width: 120,
            align: 'left',
            ellipsis: true,
            render: (text: string, record: PickupRouteWithVehicleInfoType) => (
                <Button
                    type="link"
                    style={{ padding: 0, maxWidth: '100%' }}
                    onClick={() => onShowRouteDetail && onShowRouteDetail(record)}
                >
                    <span style={{ 
                        display: 'block', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        maxWidth: '100%'
                    }}>
                        {text}
                    </span>
                </Button>
            )
        },
        {
            title: t('pickup.pickupTask.columns.zipcodes'),
            dataIndex: 'zipcodesListJson',
            key: 'zipcodesListJson',
            width: 120, // 从100增加到130
            align: 'left',
            ellipsis: true,
            render: (text: string) => {
                try {
                    // 解析JSON字符串并转换为逗号分隔的格式
                    const zipcodesArray = JSON.parse(text);
                    const displayText = Array.isArray(zipcodesArray) ? zipcodesArray.join(', ') : text;
                    return (
                        <span style={{ 
                            display: 'block', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            maxWidth: '100%'
                        }}>
                            {displayText}
                        </span>
                    );
                } catch (error) {
                    // 如果解析失败，返回原始文本
                    return (
                        <span style={{ 
                            display: 'block', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            maxWidth: '100%'
                        }}>
                            {text}
                        </span>
                    );
                }
            }
        },
        {
            title: (
                <div>
                    <div>{t('pickup.pickupTask.columns.zipcodesCount')}</div>
                    <div className="text-green-500 text-xs">
                        ({t('pickup.pickupTask.columns.total')}: {totalCount})
                    </div>
                </div>
            ),
            key: 'zipcodesCount',
            width:100,
            align: 'left',
            render: (_, record: PickupRouteWithVehicleInfoType) => {
                return record.zipcodeCount.toString();
            }
        },
        {
            title: t('pickup.pickupTask.columns.undispatchedAppointments'),
            key: 'undispatchedAppointments',
            width: 100,
            align: 'left',
            render: (_, record: PickupRouteWithVehicleInfoType) => (
                pickupAppointment ? (
                    <Button
                        type="link"
                        style={{ padding: 0 }}
                        onClick={() => onViewUndispatchedAppointments && onViewUndispatchedAppointments(record)}
                    >
                        {record.undispatchedAppointmentsCount}
                    </Button>
                ) : (
                    <span>{record.undispatchedAppointmentsCount}</span>
                )
            )
        },
        {
            title: t('pickup.pickupTask.columns.parcels'),
            key: 'parcels',
            width: 100,
            align: 'left',
            render: (_, record: PickupRouteWithVehicleInfoType) => record.totalParcelsCount
        },
        {
            title: t('pickup.pickupTask.columns.driver'),
            key: 'driver',
            width: 150,
            align: 'left',
            ellipsis: true,
            render: (_, record: PickupRouteWithVehicleInfoType) => {
                const driverName = record.driverName || '-';
                return (
                    <span style={{ 
                        display: 'block', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        maxWidth: '100%'
                    }}>
                        {driverName}
                    </span>
                );
            }
        },
        {
            title: t('pickup.pickupTask.columns.vehicle'),
            key: 'vehicle',
            width: 120,
            align: 'left',
            ellipsis: true,
            render: (_, record: PickupRouteWithVehicleInfoType) => {
                let vehicleText = '-';
                if (record.vehicleDisplayName) {
                    vehicleText = record.vehicleDisplayName;
                } else if (record.vehicleNumber) {
                    vehicleText = record.vehicleNumber;
                } else if (record.vehicleId) {
                    vehicleText = `Vehicle ${record.vehicleId}`;
                }
                
                return (
                    <span style={{ 
                        display: 'block', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        maxWidth: '100%'
                    }}>
                        {vehicleText}
                    </span>
                );
            }
        },
        {
            title: t('pickup.pickupTask.columns.vehicleModel'),
            key: 'vehicleModel',
            width: 120,
            align: 'left',
            ellipsis: true,
            render: (_, record: PickupRouteWithVehicleInfoType) => {
                const modelText = record.vehicleTypeName || '-';
                
                return (
                    <span style={{ 
                        display: 'block', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        maxWidth: '100%'
                    }}>
                        {modelText}
                    </span>
                );
            }
        },
        {
            title: t('pickup.pickupTask.columns.updateTime'),
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 120,
            align: 'left',
            render: (updatedAt: string) => (
                <span className="text-sm">{updatedAt ? formatISODateTime(updatedAt) : '--'}</span>
            ),
        },
        // {
        //     title: 'Status',
        //     dataIndex: 'status',
        //     key: 'status',
        //     width: 100,
        //     align: 'left',
        //     render: (status: string) => {
        //         const text = status || '-';
        //         return text;
        //     }
        // },
        {
            title: '',
            key: 'action',
            width: 120,
            align: 'left',
            fixed: 'right',
            render: (_, record: PickupRouteWithVehicleInfoType) => {
                // 判断是否允许取消
                // const canCancel = record.status === PickupTaskStatus.DRAFT || record.status === PickupTaskStatus.PUBLISHED;
                return (
                    <div className="space-x-2">
                        <Button
                            type="link"
                            size="small"
                            hidden={disableEdit}
                            onClick={() => onEditRoute && onEditRoute(record)}
                        >
                            Edit
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            danger
                            hidden={disableDelete}
                            onClick={() => onDeleteRoute && onDeleteRoute(record)}
                        >
                            Delete
                        </Button>
                    </div>
                );
            }
        }
    ];

    return (
        <div>
            <Table
                columns={columns}
                dataSource={data}
                loading={loading}
                rowKey="id"
                scroll={{ x: 1200 }}
                pagination={false}
                size="small"
                className="pickup-task-table mb-4"
            />
            <div className="flex justify-between items-center">
                <div className="text-gray-500">
                    {t('table.pagination.totalItems', { total: data.length })}
                </div>
            </div>
        </div>
    );
};

export default PickupTaskPlanningTable; 